import type {
  DocumentSchema,
  SearchResponse,
} from 'typesense/lib/Typesense/Documents';

import type { SetlistContext } from './client';
import {
  MOTION_GRAPHICS_CATEGORIES,
  MUSIC_COLLECTION,
  PAGE_REDIRECTS,
  SFX_COLLECTION,
} from './collections';
import type { MGCategoryItem } from './motion-graphics-types';
import type {
  SfxWithEngagementDocument,
  TracksWithEngagementDocument,
} from './tracks-with-engagement';

export interface PageRedirect {
  destination: string;
  permanent: boolean;
  source: string;
}

const SFX_VARIANTS_LIMIT = 50;
const CATEGORY_CACHE_SECONDS = 600;

// One search on one collection. A failure is reported and reads as no match.
const findDocuments = async <T extends DocumentSchema>(
  { client, onError }: SetlistContext,
  search: {
    collection: string;
    exclude_fields: string;
    filter_by: string;
    per_page: number;
  },
  report: { operation: string } & Record<string, unknown>
): Promise<T[]> => {
  try {
    const response = (await client.multiSearch.perform(
      { searches: [{ q: '*', ...search }] },
      { use_cache: true }
    )) as { results: SearchResponse<T>[] };

    return response.results.at(0)?.hits?.map((hit) => hit.document) ?? [];
  } catch (error) {
    onError(error, report);
    return [];
  }
};

export const getTrack = async (
  context: SetlistContext,
  trackSlug: string,
  contributorSlug: string
): Promise<TracksWithEngagementDocument | null> => {
  const [track] = await findDocuments<TracksWithEngagementDocument>(
    context,
    {
      collection: MUSIC_COLLECTION,
      filter_by: `track_slug:=${trackSlug} && contributor_slug:=${contributorSlug}`,
      per_page: 1,
      exclude_fields: 'id',
    },
    { operation: 'get-track-from-typesense', trackSlug, contributorSlug }
  );

  return track ?? null;
};

export const getSfx = async (
  context: SetlistContext,
  sfxId: string,
  variantId: string
): Promise<SfxWithEngagementDocument | null> => {
  const [sfx] = await findDocuments<SfxWithEngagementDocument>(
    context,
    {
      collection: SFX_COLLECTION,
      filter_by: `sfx_id:=${sfxId} && sfx_variant_id:=${variantId}`,
      per_page: 1,
      exclude_fields: 'id',
    },
    { operation: 'get-sfx-from-typesense', sfxId, variantId }
  );

  return sfx ?? null;
};

/** Every variant of an SFX, optionally without the one being viewed. */
export const getSfxVariants = (
  context: SetlistContext,
  sfxId: string,
  excludeVariantId?: string
): Promise<SfxWithEngagementDocument[]> =>
  findDocuments<SfxWithEngagementDocument>(
    context,
    {
      collection: SFX_COLLECTION,
      filter_by: excludeVariantId
        ? `sfx_id:=${sfxId} && sfx_variant_id:!=${excludeVariantId}`
        : `sfx_id:=${sfxId}`,
      per_page: SFX_VARIANTS_LIMIT,
      exclude_fields: 'id',
    },
    { operation: 'get-sfx-variants-from-typesense', sfxId, excludeVariantId }
  );

// A token match, not an exact one: callers that need an exact source must
// compare `source` themselves.
export const getPageRedirect = async (
  { client }: SetlistContext,
  source: string
): Promise<PageRedirect | null> => {
  const response = await client
    .collections<PageRedirect>(PAGE_REDIRECTS)
    .documents()
    .search(
      {
        q: '*',
        query_by: 'source',
        filter_by: `source:${source}`,
        exhaustive_search: false,
        use_cache: true,
      },
      { cacheSearchResultsForSeconds: CATEGORY_CACHE_SECONDS }
    );

  return response?.hits?.[0]?.document ?? null;
};

/** The category behind a motion graphics or LUT category page. */
export const getMotionGraphicsCategory = async (
  { client }: SetlistContext,
  relativeUrl: string
): Promise<MGCategoryItem | null> => {
  const response = await client
    .collections<MGCategoryItem>(MOTION_GRAPHICS_CATEGORIES)
    .documents()
    .search(
      {
        q: '*',
        query_by: 'relative_url',
        filter_by: `relative_url:${relativeUrl}`,
        exhaustive_search: false,
      },
      { cacheSearchResultsForSeconds: CATEGORY_CACHE_SECONDS }
    );

  return response?.hits?.[0]?.document ?? null;
};
